/**
 * Chuẩn hoá lỗi trả về từ `apiProductionPlanning`.
 *
 * ⚠️ Interceptor của `createApiServices` (services/api/createApiServices.js) TRẢI PHẲNG lỗi có
 *    thân JSON: nó reject bằng `{...data, status, message}` — object thường, **không còn
 *    `.response`**. Vì service NestJS luôn trả thân JSON, đây là đường đi của mọi lỗi 4xx/5xx.
 *
 *    Chỉ khi thân lỗi KHÔNG phải JSON (trang lỗi HTML của proxy, timeout, lỗi mạng) thì
 *    `AxiosError` mới đi qua nguyên vẹn và còn `.response`.
 *
 * Nên mọi nơi đọc lỗi phải đi qua các hàm ở đây, tuyệt đối không đọc `error.response.status`
 * trực tiếp — đọc thẳng như vậy là code chết cho toàn bộ lỗi của service.
 */

/** Thông báo mặc định của axios — hiện nguyên văn cho người dùng thì vô nghĩa. */
const isAxiosDefaultMessage = (text) => !text
    || text === 'Network Error'
    || text.indexOf('Request failed with status code') === 0
    || text.indexOf('timeout of') === 0

/** Thân lỗi của service, ở cả hai dạng phẳng và nguyên `AxiosError`. */
const bodyOf = (error) => (error && error.response && error.response.data) || {}

/** HTTP status. `null` khi không xác định được (lỗi mạng, request bị huỷ). */
export const errorStatus = (error) => {
    if (!error) return null

    if (error.status !== undefined && error.status !== null) return error.status

    return bodyOf(error).status || (error.response ? error.response.status : null) || null
}

/** Mã lỗi nghiệp vụ của service — `STAGE_CONFIG_STALE`, `M18_E005`, ... */
export const errorCode = (error) => {
    if (!error) return null

    return error.code || bodyOf(error).code || null
}

/**
 * Thông báo do service gửi kèm. Trả `null` khi service không gửi gì, để màn hình dùng câu tiếng
 * Việt mặc định của mình thay vì hiện thông báo kỹ thuật tiếng Anh của axios.
 */
export const errorMessage = (error) => {
    if (!error) return null

    if (!isAxiosDefaultMessage(error.message)) return error.message

    const fromBody = bodyOf(error).message

    return isAxiosDefaultMessage(fromBody) ? null : fromBody
}

/** Danh sách chi tiết kèm lỗi — dùng cho `RATE_CHANGE_REASON_REQUIRED`, `AMBIGUOUS_PAIR`. */
/**
 * Chi tiết lỗi nghiệp vụ (danh sách vi phạm theo nhóm/dòng).
 *
 * ⚠️ Service gửi dưới khoá **`data`** (`AllExceptionsFilter` dựng `{success, code, message, data}`),
 *    không phải `details`. Bản cũ chỉ đọc `details` nên hàm này LUÔN trả `[]` — hộp thoại nhập lý do
 *    thay đổi không bao giờ mở được, và người dùng chỉ thấy ô đỏ chung chung. Đọc cả hai khoá để
 *    không phụ thuộc một tên duy nhất.
 */
export const errorDetails = (error) => {
    if (!error) return []

    const body = bodyOf(error)
    const found = error.details || error.data || body.details || body.data || []

    return Array.isArray(found) ? found : []
}

/**
 * Mã LUẬT M16 (`VAL-05`, `VAL-09`, `VAL-18`…) — nằm trong từng phần tử của `data`, KHÔNG ở top-level.
 *
 * ⚠️ `errorCode()` trả mã ENVELOPE (`VALIDATION_ERROR`, `CONFLICT`, `NOT_FOUND`…). Hai thang mã khác
 *    nhau và rất dễ lẫn: so `errorCode(e)` với tên luật là một nhánh KHÔNG BAO GIỜ chạy, nên hộp
 *    thoại nhập lý do thay đổi không mở được và người dùng kẹt cứng — không có đường nào lưu nổi
 *    công đoạn đó nữa. Phân nhánh theo luật thì phải đi qua hàm này.
 *
 * Dùng `VAL_CODES` trong `constants/` chứ đừng gõ chuỗi tay: gõ nhầm cũng chỉ ra một nhánh chết y hệt.
 */
export const hasDetailCode = (error, code) => errorDetails(error).some((item) => item && item.code === code)
